import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useForgotPassword } from '@/hooks/useApi';

export default function ForgotPasswordPage() {
  const { mutate: forgot, isLoading } = useForgotPassword();
  const [email, setEmail]   = useState('');
  const [sent, setSent]     = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    forgot(email, { onSuccess: () => setSent(true) });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 dark:from-brand-900/40 to-accent-50 dark:to-zinc-950 px-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-gray-200 dark:border-zinc-800 p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 text-white text-2xl mb-4 shadow-lg dark:shadow-black/40">
            🃏
          </div>
          <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-zinc-100">Reset your password</h1>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">
            {sent ? 'Check your inbox' : "Enter your email and we'll send a reset link"}
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/40 mx-auto">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-gray-600 dark:text-zinc-400 text-sm">
              If an account with that email exists, a password reset link has been sent. Check your spam folder if you don't see it.
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-brand-600 dark:text-brand-400 text-sm font-medium hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-800 focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3 disabled:opacity-50"
            >
              {isLoading ? 'Sending…' : 'Send reset link'}
            </button>
            <div className="text-center">
              <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
